import { useMutation, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { BadgeCheck, KeyRound } from "lucide-react";
import { type FormEvent, type ReactNode, useRef, useState } from "react";

import { api, ApiError } from "../api/client";
import type { LicenceStatus } from "../api/types";
import { GenerexMark, GenerexWordmark } from "../components/GenerexMark";
import { attentionSelector, designate } from "../components/attention";
import { Callout } from "../components/Callout";
import { PropRow } from "../components/panel";
import { Motion, Presence } from "../components/presence";
import { BetaBadge, Button, Field, inputClass } from "../components/ui";
import {
  ACTIVATION_HINTS,
  formatDateTime,
  formatLicenceCode,
  formatLongDate,
  LICENCE_QUERY,
  licenceTypeLabel,
  previousPoste,
} from "./licence";
import { isPreprod, useEnvironmentValue } from "../app/environment";
import { t, tServer } from "../i18n/i18n";
import { LangSwitch } from "../i18n/LangSwitch";
import { licenceScope } from "../state/licenceScope";
import { unsavedDrafts } from "../state/unsavedDrafts";
import { licenceRefusalText } from "./LicenceFooter";
import { Waiting } from "../components/Waiting";

/** Cadre commun aux écrans de licence : emblème, titre, carte centrée (entrée en léger zoom, R11). `kept` : écran
 *  affiché par-dessus l'application (licence perdue en cours de session) : sans choix de langue, dont le
 *  changement remonterait toute l'application (`Localized`, main.tsx) et lui ferait perdre ce qu'elle garde. */
export function LicenceFrame({ children, wide = false, kept = false }: { children: ReactNode; wide?: boolean; kept?: boolean }) {
  return (
    <div className="flex min-h-full items-center justify-center bg-canvas p-6">
      <Motion
        origin="zoom"
        className={clsx("w-full rounded-xl border border-line bg-surface p-6 shadow-float", wide ? "max-w-[680px]" : "max-w-[400px]")}
      >
        <div className="mb-6 flex items-center gap-2.5">
          <GenerexMark size={32} />
          <GenerexWordmark />
          <BetaBadge />
          {!kept && <LangSwitch className="ml-auto" />}
        </div>
        {children}
      </Motion>
    </div>
  );
}

/** Écran d'activation, comme `LicenceActivationView` de l'iPad : saisie du code de licence (GEN-… en ligne,
 *  GENOFF-… hors ligne), puis récapitulatif de la licence activée avant d'accéder à Generex. `kept` : affiché
 *  par-dessus l'application (licence perdue en cours de session), dont les saisies en cours sont gardées. */
export function ActivationScreen({ status, kept = false }: { status: LicenceStatus; kept?: boolean }) {
  const queryClient = useQueryClient();
  // Préproduction (registre F35) : l'activation y occupe une place BELLICOM de la licence, comme en production.
  const preprod = useEnvironmentValue(isPreprod);
  // Poste inconnu du serveur alors que ce navigateur a déjà été activé : le cookie du poste a été perdu.
  const [trace] = useState(() => (status.reason === "none" ? previousPoste() : null));
  const [code, setCode] = useState("");
  const [deviceName, setDeviceName] = useState(trace?.name ?? "");
  const [activated, setActivated] = useState<LicenceStatus | null>(null);
  const [newCode, setNewCode] = useState(false);
  const nameInput = useRef<HTMLInputElement>(null);
  // Licence en mode comptes (docs/17 §5.7) : la reprise de place se confirme avec les identifiants d'un
  // administrateur de l'unité, saisis ici et envoyés avec la demande seulement.
  const [adminLogin, setAdminLogin] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [adminMode, setAdminMode] = useState(false);
  const [inUseKept, setInUseKept] = useState<Record<string, unknown> | null>(null);
  // `replace` : reprendre la place d'un poste de même nom sur la licence (registre F25).
  const activate = useMutation({
    mutationFn: (replace: boolean) =>
      api.activateLicence(code, deviceName, replace, replace && adminMode ? { login: adminLogin.trim(), password: adminPassword } : undefined),
    onSuccess: (next) => {
      setActivated(next);
      setNewCode(false);
      setCode("");
      setAdminPassword("");
      setAdminMode(false);
      setInUseKept(null);
    },
    onError: (error) => {
      setAdminPassword("");
      if (!(error instanceof ApiError)) return;
      if (error.code === "TERMINAL_NAME_IN_USE") {
        setInUseKept(error.detail ?? {});
        if (error.detail?.accounts === true) setAdminMode(true);
      }
      if (error.code === "REPLACE_NEEDS_ADMIN") setAdminMode(true);
    },
  });
  const enter = () => {
    if (!activated) return;
    queryClient.setQueryData(LICENCE_QUERY, activated);
    // Les requêtes refusées avant l'activation sont relancées.
    void queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] !== LICENCE_QUERY[0] });
  };

  if (activated && !newCode) {
    // Licence perdue en cours de session, réactivée avec le code d'une AUTRE licence : les saisies gardées pour la
    // précédente seront effacées de ce navigateur en accédant à Generex (`licenceScope.adopt`) ; c'est dit avant.
    const erases =
      kept && licenceScope.differs(activated.space_ref) && (unsavedDrafts.list().length > 0 || licenceScope.storedDrafts() > 0);
    return (
      <LicenceFrame kept={kept}>
        <h1 className="flex items-center gap-2 text-title font-semibold text-ok">
          <BadgeCheck size={16} /> {t("Licence activée")}
        </h1>
        <div className="mt-3 rounded-lg border border-line px-3">
          <PropRow label={t("Type de licence")}>{licenceTypeLabel(activated)}</PropRow>
          <PropRow label={t("Terminaux autorisés")}>{activated.max_devices ?? "—"}</PropRow>
          {activated.activated_devices != null && <PropRow label={t("Terminaux activés")}>{activated.activated_devices}</PropRow>}
          <PropRow label={t("Code")} mono>
            {activated.licence_code}
          </PropRow>
          <PropRow label={t("Expire le")}>{formatLongDate(activated.expires_at)}</PropRow>
        </div>
        {erases && (
          <Callout tone="warn" className="mt-3" title={t("Ce code ouvre une autre licence.")}>
            {t("Les saisies non enregistrées de la licence précédente seront effacées de ce navigateur en accédant à Generex. Pour les garder, saisissez de nouveau le code de la licence précédente.")}
          </Callout>
        )}
        <div className="mt-4 space-y-2">
          <Button className="w-full" onClick={enter}>
            {t("Accéder à Generex")}
          </Button>
          <Button variant="secondary" className="w-full" onClick={() => setNewCode(true)}>
            {t("Saisir un nouveau code de licence")}
          </Button>
        </div>
      </LicenceFrame>
    );
  }

  const failure = activate.error instanceof ApiError ? activate.error : null;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (code.trim().length >= 4 && !activate.isPending) activate.mutate(false);
  };
  // Un poste de même nom existe sur la licence : sans doute ce même appareil, dont le navigateur a effacé ses données.
  // Mode comptes : la fiche reste affichée pendant la saisie des identifiants d'administrateur (refus compris).
  const adminRefused = adminMode && failure !== null && ["REPLACE_NEEDS_ADMIN", "AUTH_FAILED", "TOO_MANY_ATTEMPTS"].includes(failure.code ?? "");
  const inUse = failure?.code === "TERMINAL_NAME_IN_USE" ? failure.detail : adminRefused ? (inUseKept ?? failure?.detail) : undefined;
  const adminProblem =
    adminMode && failure?.code === "AUTH_FAILED"
      ? t("Identifiant ou mot de passe d'administrateur incorrect.")
      : adminMode && failure?.code === "TOO_MANY_ATTEMPTS" && failure.detail?.kind !== "replace-space"
        ? // Délai d'un identifiant sur ce poste (5 échecs), avant celui de la licence (10) : texte du serveur, déjà traduit.
          failure.message
        : adminMode && failure?.code === "TOO_MANY_ATTEMPTS"
        ? t("Trop d'essais d'identifiants d'administrateur pour cette licence : réessayez dans {n} min. Les administrateurs de l'unité en sont prévenus.", {
            n: Math.max(1, Math.ceil(Number(failure.detail?.retry_after ?? 3600) / 60)),
          })
        : null;
  const adminMissing = !adminLogin.trim() ? "licence-admin-login" : !adminPassword ? "licence-admin-password" : null;
  const inUseCount = typeof inUse?.count === "number" ? inUse.count : 1;
  const inUseName = typeof inUse?.name === "string" ? inUse.name : deviceName.trim();
  const inUseWhen = typeof inUse?.last_seen === "string" ? formatDateTime(inUse.last_seen) : "—";
  // Code trop court (R12) : « Activer » reste cliquable et désigne le champ du code.
  const codeMissing = code.trim().length < 4;
  const notice = !activated && status.reason !== "none" && status.message ? tServer(status.message) : null;
  return (
    <LicenceFrame kept={kept}>
      <h1 className="flex items-center gap-2 text-title font-semibold text-ink">
        <KeyRound size={16} className="text-ink-muted" /> {t("Activez votre licence")}
      </h1>
      <p className="mt-1.5 text-ui leading-relaxed text-ink-soft">
        {t("Generex est réservé aux clients disposant d'une licence active. Saisissez le code de licence qui vous a été transmis.")}
      </p>

      {preprod && (
        <div data-preprod-activation>
          <Callout tone="info" className="mt-3" title={t("Préproduction : l'activation occupe une place de la licence, comme en production.")}>
            {t("N'activez ici que votre propre licence, jamais celle d'un client.")}
          </Callout>
        </div>
      )}
      <Presence show={Boolean(notice)}>{notice && <Callout tone="warn" className="mt-3">{notice}</Callout>}</Presence>
      {kept && (
        <Callout tone="info" className="mt-3">
          {t("Vos saisies en cours sont gardées sur ce poste : vous les retrouverez telles quelles après la réactivation, avec le même code de licence. Le code d'une autre licence les effacerait de ce navigateur.")}
        </Callout>
      )}
      <Presence show={!activated && Boolean(trace)}>
        {!activated && trace && (
          <Callout
            tone="warn"
            className="mt-3"
            title={
              trace.name
                ? t("Ce navigateur était déjà activé sous le nom « {name} » (dernière utilisation : {when}), mais il n'a pas conservé l'identifiant du poste : saisissez de nouveau le code.", { name: trace.name, when: formatDateTime(trace.seenAt) })
                : t("Ce navigateur était déjà activé (dernière utilisation : {when}), mais il n'a pas conservé l'identifiant du poste : saisissez de nouveau le code.", { when: formatDateTime(trace.seenAt) })
            }
          >
            {t("Si cela se reproduit, vérifiez que la navigation privée n'est pas active et que le navigateur accepte les cookies de ce site (sur iPad : Réglages › Apps › Safari, « Bloquer tous les cookies » désactivé).")}
          </Callout>
        )}
      </Presence>

      <form onSubmit={submit} className="mt-4 space-y-3">
        <Field
          label={t("Code de licence")}
          hint={t("Saisissez le code de licence fourni par BELLICOM — les tirets s'ajoutent automatiquement.")}
        >
          <input
            data-attention="licence-code"
            className={clsx(inputClass, "font-mono tracking-wide")}
            placeholder={t("Code de licence")}
            value={code}
            onChange={(e) => setCode(formatLicenceCode(e.target.value))}
            autoFocus
            autoComplete="off"
            spellCheck={false}
          />
        </Field>
        <Field
          label={t("Nom du poste")}
          hint={t("Facultatif, mais conseillé : il figure dans la liste des postes de la licence, et un poste nommé qui perd son activation reprend sa place sans en occuper une nouvelle.")}
        >
          <input
            ref={nameInput}
            className={inputClass}
            placeholder={t("Ex. : PC CDU 3")}
            value={deviceName}
            maxLength={120}
            onChange={(e) => setDeviceName(e.target.value)}
          />
        </Field>

        <Presence show={Boolean(inUse)}>
          {inUse && (
            <Callout
              tone="warn"
              title={
                adminMode
                  ? t("Un poste « {nom} » est déjà activé sur cette licence. L'unité utilise des comptes nominatifs : un administrateur de l'unité doit confirmer la reprise de sa place.", { nom: inUseName })
                  : inUseCount > 1
                    ? t("{count} postes nommés « {name} » sont déjà activés sur cette licence (dernière utilisation : {when}).", { count: inUseCount, name: inUseName, when: inUseWhen })
                    : t("Un poste nommé « {name} » est déjà activé sur cette licence (dernière utilisation : {when}).", { name: inUseName, when: inUseWhen })
              }
            >
              <p>
                {t("Si c'est cet appareil, dont le navigateur a effacé ses données, reprenez sa place : l'ancien poste est libéré et la licence ne perd aucune place. Sinon, donnez un autre nom à ce poste.")}
              </p>
              {adminMode && (
                <div className="mt-2.5 space-y-2.5">
                  <Field label={t("Identifiant de l'administrateur")}>
                    <input
                      data-attention="licence-admin-login"
                      className={inputClass}
                      value={adminLogin}
                      maxLength={40}
                      onChange={(e) => setAdminLogin(e.target.value)}
                      autoComplete="off"
                      autoCapitalize="none"
                      spellCheck={false}
                    />
                  </Field>
                  <Field label={t("Mot de passe")}>
                    <input
                      type="password"
                      data-attention="licence-admin-password"
                      className={inputClass}
                      value={adminPassword}
                      maxLength={128}
                      onChange={(e) => setAdminPassword(e.target.value)}
                      autoComplete="off"
                    />
                  </Field>
                  <Presence show={Boolean(adminProblem)}>{adminProblem && <Callout tone="danger">{adminProblem}</Callout>}</Presence>
                </div>
              )}
              <div className="mt-2.5 flex flex-wrap gap-2">
                <Button
                  type="button"
                  onClick={() => activate.mutate(true)}
                  disabled={activate.isPending}
                  blocked={adminMode && Boolean(adminMissing)}
                  onBlocked={() => adminMissing && designate(attentionSelector(adminMissing))}
                >
                  {activate.isPending ? <Waiting>{t("Activation en cours…")}</Waiting> : adminMode ? t("Reprendre la place") : t("Reprendre sa place")}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    activate.reset();
                    setAdminMode(false);
                    setInUseKept(null);
                    nameInput.current?.focus();
                    nameInput.current?.select();
                  }}
                >
                  {t("Changer de nom")}
                </Button>
              </div>
            </Callout>
          )}
        </Presence>
        <Presence show={Boolean(activate.error) && !inUse}>
          {activate.error && !inUse && (
            <Callout tone="danger" title={(failure && licenceRefusalText(failure)) || t("Activation impossible. Réessayez plus tard.")}>
              {failure?.code === "DEVICE_LIMIT_REACHED" && failure.detail?.accounts === true
                ? t("Toutes les places de la licence sont prises : demandez à un administrateur de l'unité de libérer un poste, ou de reprendre la place de l'ancien.")
                : failure?.code && ACTIVATION_HINTS[failure.code]
                  ? t(ACTIVATION_HINTS[failure.code] ?? "")
                  : undefined}
            </Callout>
          )}
        </Presence>

        {!inUse && (
          <Button
            type="submit"
            className="w-full"
            disabled={activate.isPending}
            blocked={codeMissing}
            onBlocked={() => designate(attentionSelector("licence-code"))}
          >
            {activate.isPending ? <Waiting>{t("Activation en cours…")}</Waiting> : t("Activer")}
          </Button>
        )}
        {activated && (
          <Button variant="secondary" className="w-full" onClick={() => setNewCode(false)}>
            {t("Conserver la licence active")}
          </Button>
        )}
      </form>
    </LicenceFrame>
  );
}
